import { Component, EventEmitter, Input, Output, SimpleChanges, OnChanges } from '@angular/core'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { ScopeVersion } from '@core/model/scope-version'
import { ApprovalFlowService } from '@app/features/scope-overview/service/approval-flow.service'
import { User } from '@app/core/model/user.model'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { CommonModule } from '@angular/common'
import { SharedModule } from '@app/shared/shared.module'
import { ScopeUiTableComponent } from '@app/shared/components/ui-components/scope-ui-table/scope-ui-table.component'
import { NgxMaskDirective } from 'ngx-mask'
import { ScopeTabService } from '@app/features/scope-overview/service/scope-tab.service'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { Role } from '@app/features/scoping/models/role.model'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { ScopeUiInputComponent } from '@shared/components/ui-components/scope-ui-input/scope-ui-input.component'
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop'
import { select, Store } from '@ngrx/store'
import { ToggleListComponent } from '@shared/components/ui-components/toggle-list/toggle-list.component'
import { Preference } from '@core/model/user-preferences.interface'
import { BootstrapActions } from '@core/store'
import { cloneDeep, isNil } from 'lodash'
import { ScopeOverviewActions } from '@app/features/scope-overview/store/actions/scope-overview.action'
import { SectionRowComponent } from '@app/features/scope-overview/components/scope-tab/section-row/section-row.component'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import { filter } from 'rxjs/operators'
import { Observable, take } from 'rxjs'
import { MenuOptions } from '@core/model/definitions/menu-options.interface'
import { formatDisplayHoursToMinutes, formatHours, trackById } from '@shared/utils/utils'
import { CdnConfig } from '@core/model/cdn-config.model'
import { ScopeElementsModule } from '@app/shared/components/scope-elements/scope-elements.module'
import { Privilege } from '../../../../../core/model/enums/privilege.enum'
import { ExchangeRateService } from '@app/shared/services/exchange-rate.service'
import { ScopeUiModalComponent } from '@app/shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { MatDialog } from '@angular/material/dialog'
import { ModalConfig } from '@app/core/model/modal-config.model'
import { LanguageService } from '@app/core/service/language.service'
import { FormControl } from '@angular/forms'
import { ApplicationSetting } from '@app/core/model/enums/application-setting.enum'
import { AuthService } from '@app/core/service/auth.service'
import { ScopeOverviewService } from '@app/features/scope-overview/service/scope-overview.service'
import { ScopePaginatedOverviewActions } from '@app/features/scope-overview/store/actions/scope-paginated-overview.actions'
import {
  ScopeElementsConfig,
  UpdateDeliverablePayload,
  UpdateFeeItemPayload,
  UpdateRolePayload,
  UpdateTpcPayload,
} from '@app/shared/components/scope-elements/models'
import { UpdateComponentPayload } from '@app/shared/components/scope-elements/models/update-component-payload.interface'
import { ChangeRequestSelectors } from '@app/features/scope-overview/change-request/store/selectors/change-request.selectors'
import { ScopeElementsService } from '@app/shared/components/scope-elements/services/scope-elements.service'
import { SnackbarService } from '@app/shared/utils/snackbar.service'
import { plainToInstance } from 'class-transformer'
import { InfiniteScrollModule } from 'ngx-infinite-scroll'

import { mapScopeDeliverableToPlainApi } from '@app/features/scope-overview/utils/map-scope-deliverable.utils'
import { hasFixedCostDeliverableEditPrivileges } from '@app/shared/components/scope-elements/utils/has-fixed-cost-edit-privileges.util'
import { ChangeRequestTab } from '@app/features/scope-overview/change-request/enum'
import { ChangeRequestNavigationService } from '@app/features/scope-overview/change-request/service'
import { MoveDeliverablePayload } from '@app/features/scope-overview/model/move-deliverable.payload.model'

@Component({
  selector: 'scope-table',
  imports: [
    CommonModule,
    SharedModule,
    ScopeUiTableComponent,
    NgxMaskDirective,
    ScopeUiInputComponent,
    ToggleListComponent,
    SectionRowComponent,
    ScopeElementsModule,
    InfiniteScrollModule,
  ],
  templateUrl: './scope-table.component.html',
  styleUrls: ['./scope-table.component.scss'],
  standalone: true,
})
export class ScopeTableComponent implements OnChanges {
  @Input() isTimeline = false
  @Input() isEditable?: boolean
  @Input() isChangeRequest?: boolean
  @Input() currentUser!: User
  @Input() scopeVersion!: ScopeVersion
  @Input() feeMenuOptions!: MenuOptions[]
  @Input() currentRatecard: RatecardVersion
  @Input() mainColumnClass: string
  @Input() componentMainColumnClass: string
  @Input() roleMainColumnClass: string
  @Input() sortedSections: ScopeSection[] = []
  @Input() sortedDefaultSectionDeliverables: Deliverable[] = []
  @Input() cdnConfig?: CdnConfig
  @Input() deliverableMenuOptions: MenuOptions[]
  @Input() componentMenuOptions: MenuOptions[]

  @Output() onFetchDeliverable = new EventEmitter<number>()
  @Output() onUpdateDeliverable = new EventEmitter<UpdateDeliverablePayload>()
  @Output() onUpdateComponent = new EventEmitter<UpdateComponentPayload>()
  @Output() onAcceptDeliverable = new EventEmitter<Deliverable>()
  @Output() onRejectDeliverable = new EventEmitter<Deliverable>()
  @Output() onDuplicateDeliverable = new EventEmitter<Deliverable>()
  @Output() onDeleteDeliverable = new EventEmitter<Deliverable>()
  @Output() onAddDeliverableFee = new EventEmitter<Deliverable>()
  @Output() onAddComponentFee = new EventEmitter<ScopeComponent>()
  @Output() onDeleteComponent = new EventEmitter<ScopeComponent>()
  @Output() onEditDeliverable = new EventEmitter<number>()
  @Output() onCompleteTrade = new EventEmitter<Deliverable>()
  @Output() onTradeDeliverable = new EventEmitter<Deliverable>()
  @Output() onStopProgressTrade = new EventEmitter<Deliverable>()
  @Output() onAddDeliverableToTrade = new EventEmitter<Deliverable>()
  @Output() onConfirmTrade = new EventEmitter<Deliverable>()
  @Output() onCancelTrade = new EventEmitter<Deliverable>()
  @Output() onReopenTrade = new EventEmitter<Deliverable>()
  @Output() onShowDeliverableApplyLocationCardDialog = new EventEmitter<Deliverable>()
  @Output() onUpdateFeeItem = new EventEmitter<UpdateFeeItemPayload>()
  @Output() onAddSectionFee = new EventEmitter<ScopeSection>()
  @Output() onDeleteSection = new EventEmitter<ScopeSection>()
  @Output() onMoveDeliverable = new EventEmitter<MoveDeliverablePayload>()
  @Output() onMoveComponent = new EventEmitter<any>()
  @Output() onMoveComponentOutsideDeliverable = new EventEmitter<ScopeComponent>()
  @Output() onUpdateRole = new EventEmitter<UpdateRolePayload>()

  sectionDragging: boolean

  isDeliverablesLoading$ = this.store.select(ScopeOverviewSelectors.selectIsDeliverablesLoading)
  isSectionsLoading$ = this.store.select(ScopeOverviewSelectors.selectIsSectionsLoading)

  sectionLoadingMap$ = (id: number): Observable<boolean> =>
    this.store.select(ScopeOverviewSelectors.selectSectionLoading(id))

  constructor(
    public scopeApprovalFlowService: ApprovalFlowService,
    public scopeTabService: ScopeTabService,
    protected scopeElementsService: ScopeElementsService,
    private store: Store,
    private authService: AuthService,
    protected exchangeRateService: ExchangeRateService,
    private dialog: MatDialog,
    private lang: LanguageService,
    private scopeOverviewService: ScopeOverviewService,
    private snackbarService: SnackbarService,
    private changeRequestNavigationService: ChangeRequestNavigationService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.scopeVersion) this.setComponentMenuOptions()
  }

  addSectionFee(section: ScopeSection) {
    this.onAddSectionFee.emit(section)
  }

  deleteSection(section: ScopeSection) {
    this.onDeleteSection.emit(section)
  }

  fetchDeliverable(deliverableId: number) {
    this.onFetchDeliverable.emit(deliverableId)
  }

  updateDeliverable(payload: UpdateDeliverablePayload) {
    this.onUpdateDeliverable.emit(payload)
  }

  updateComponent(payload: UpdateComponentPayload) {
    this.onUpdateComponent.emit(payload)
  }

  deleteDeliverable(deliverable: Deliverable) {
    this.onDeleteDeliverable.emit(deliverable)
  }

  addDeliverableFee(deliverable: Deliverable) {
    this.onAddDeliverableFee.emit(deliverable)
  }

  addComponentFee(component: ScopeComponent) {
    this.onAddComponentFee.emit(component)
  }

  deleteComponent(component: ScopeComponent) {
    this.onDeleteComponent.emit(component)
  }

  editDeliverable(deliverableId: number) {
    this.onEditDeliverable.emit(deliverableId)
  }

  showDeliverableApplyLocationCardDialog(deliverable: Deliverable) {
    this.onShowDeliverableApplyLocationCardDialog.emit(deliverable)
  }

  updateFeeItem(payload: UpdateFeeItemPayload) {
    this.onUpdateFeeItem.emit(payload)
  }

  getDeliverables() {
    this.store.dispatch(ScopePaginatedOverviewActions.showNextBufferedDeliverables())
  }

  getSections() {
    this.store.dispatch(ScopePaginatedOverviewActions.getScopeSections())
  }

  getSectionDeliverables(sectionId: number) {
    this.store.dispatch(ScopePaginatedOverviewActions.getScopeSectionDeliverables({ sectionId }))
  }

  overrideQuantity(component: ScopeComponent) {
    const data: ModalConfig = {
      title: `Change ${this.lang.get('component')} Quantity`,
      body: `Enter the new quantity for ${component.source.name + (component.name ? ' ~ ' + component.name : '')}`,
      confirmText: 'Confirm change',
      confirmCallback: (form: FormControl) => {
        dialog.close()
        const updatedComponent = plainToInstance(ScopeComponent, {
          ...component,
          quantity: form.get('quantity')?.value,
        })

        this.scopeOverviewService
          .updateScopeComponent(this.scopeVersion.identity.id, component.deliverable?.id, updatedComponent, true)
          .subscribe({
            next: (update: ScopeComponent) => {
              updatedComponent.comments = updatedComponent.comments.concat(update.comments)
              this.store.dispatch(ScopeOverviewActions.overrideScopeComponentSuccess({ component: updatedComponent }))
            },
            error: (error) => {
              this.snackbarService.showDefaultErrorSnackbar()
              console.error(error)
              return error
            },
          })
      },
      inputs: [{ name: 'quantity', control: new FormControl(component.quantity), type: 'number', label: 'Quantity' }],
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data })
  }

  updateDeliverableTpc({ deliverable, thirdPartyCost }: UpdateTpcPayload) {
    this.store.dispatch(ScopeOverviewActions.updateDeliverableTpc({ deliverable, thirdPartyCost }))
  }

  updateComponentTpc({ component, deliverable, thirdPartyCost }: UpdateTpcPayload) {
    this.store.dispatch(ScopeOverviewActions.updateComponentTpc({ component, deliverable, thirdPartyCost }))
  }

  updateDeliverableSectionTpc({ deliverable, section, thirdPartyCost }: UpdateTpcPayload) {
    this.store.dispatch(ScopeOverviewActions.updateDeliverableSectionTpc({ deliverable, section, thirdPartyCost }))
  }

  reorderDeliverableTpcs({ deliverable, thirdPartyCosts }) {
    thirdPartyCosts.forEach((tpc, index) => (tpc.order = index))
    this.scopeOverviewService
      .reorderDeliverableThirdPartyCosts(this.scopeVersion.identity.id, deliverable.id, thirdPartyCosts)
      .subscribe({
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          console.error(error)
        },
      })
  }

  reorderComponentTpcs({ component, thirdPartyCosts }) {
    thirdPartyCosts.forEach((tpc, index) => (tpc.order = index))
    this.scopeOverviewService
      .reorderComponentThirdPartyCosts(this.scopeVersion.identity.id, component.id, thirdPartyCosts)
      .subscribe({
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          console.error(error)
        },
      })
  }

  reorderDeliverableSectionTpcs({ deliverable, section, thirdPartyCosts }) {
    thirdPartyCosts.forEach((tpc, index) => (tpc.order = index))
    this.scopeOverviewService
      .reorderDeliverableSectionThirdPartyCosts(
        this.scopeVersion.identity.id,
        deliverable.id,
        section.id,
        thirdPartyCosts
      )
      .subscribe({
        error: (error) => {
          this.snackbarService.showDefaultErrorSnackbar()
          console.error(error)
        },
      })
  }

  updateRole({ component, role }: UpdateRolePayload) {
    if (this.isChangeRequest) {
      this.onUpdateRole.emit({ component, role })
    } else {
      this.store.dispatch(ScopeOverviewActions.overrideRole({ component, role }))
    }
  }

  overrideRateCardRoleHours({ component, role }: UpdateRolePayload) {
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Change Role Hours`,
        `Enter the new hours for ${role.name}`,
        'Confirm change',
        undefined,
        (form: FormControl) => {
          dialog.close()
          const minutes = formatDisplayHoursToMinutes(form.get('rateCardMinutes')?.value)
          const updatedRole = plainToInstance(Role, { ...role, rateCardMinutes: minutes })

          this.scopeOverviewService
            .updateComponentRole(this.scopeVersion.identity.id, component.id, updatedRole, true)
            .subscribe({
              next: () => {
                this.store.dispatch(
                  ScopeOverviewActions.overrideRoleSuccess({ component: component, role: updatedRole })
                )
              },
              error: (error) => {
                this.snackbarService.showDefaultErrorSnackbar()
                console.error(error)
                return error
              },
            })
        },
        undefined,
        [
          {
            name: 'rateCardMinutes',
            control: new FormControl(formatHours(role.rateCardMinutes / 60)),
            type: this.authService.loggedInUser.company.hasApplicationSetting(
              ApplicationSetting.SCOPE__AGENCY_HOURS_AND_MINUTES
            )
              ? 'hoursMinutes'
              : 'hours',
            label: `${this.lang.get('agency')} Hours`,
          },
        ]
      ),
    })
  }

  completeTrade(deliverable: Deliverable) {
    this.onCompleteTrade.emit(deliverable)
  }

  tradeDeliverable(deliverable: Deliverable) {
    this.onTradeDeliverable.emit(deliverable)
  }

  stopProgressTrade(deliverable: Deliverable) {
    this.onStopProgressTrade.emit(deliverable)
  }

  addDeliverableToTrade(deliverable: Deliverable) {
    this.onAddDeliverableToTrade.emit(deliverable)
  }

  confirmTrade(deliverable: Deliverable) {
    this.onConfirmTrade.emit(deliverable)
  }

  cancelTrade(deliverable: Deliverable) {
    this.onCancelTrade.emit(deliverable)
  }

  reopenTrade(deliverable: Deliverable) {
    this.onReopenTrade.emit(deliverable)
  }

  acceptDeliverable(deliverable: Deliverable) {
    this.onAcceptDeliverable.emit(deliverable)
  }

  rejectDeliverable(deliverable: Deliverable) {
    this.onRejectDeliverable.emit(deliverable)
  }

  onToggleScopePreference(preference: Preference) {
    this.store.dispatch(BootstrapActions.updateScopeOverviewColumnPreferences({ updatedPrefs: preference }))
  }

  dropSection(event: CdkDragDrop<any>) {
    const sections = cloneDeep(event.container.data)
    moveItemInArray(sections, event.previousIndex, event.currentIndex)
    sections.forEach((d, i) => (d.order = i))
    this.store.dispatch(
      ScopeOverviewActions.updateScopeSectionsOrder({ scopeId: this.scopeVersion.identity.id, sections: sections })
    )
  }

  get sectionDrops() {
    const str: string[] = []
    this.scopeVersion.sections.forEach((s) => {
      str.push('list-' + s.id)
    })
    str.push('list-one')
    return str
  }

  dropDeliverable(event: CdkDragDrop<any>, section: ScopeSection | null) {
    if (this.isChangeRequest) {
      const deliverable: Deliverable = event.item.data

      if (section)
        if (!this.scopeTabService.sectionSelectedStates[section.id]) this.scopeTabService.toggleSection(section.id)

      this.onUpdateDeliverable.emit({
        deliverable: mapScopeDeliverableToPlainApi(deliverable),
        changes: {
          section: {
            old: deliverable.section,
            new: section?.id ? ({ id: section.id } as ScopeSection) : null,
          },
        },
      })

      return
    }

    const deliverables = cloneDeep(event.container.data)
    if (event.previousContainer != event.container) {
      const previousSectionDeliverables = cloneDeep(event.previousContainer.data)

      transferArrayItem(previousSectionDeliverables, deliverables, event.previousIndex, event.currentIndex)
      this.updateScopeDeliverableOrder(previousSectionDeliverables, deliverables[event.currentIndex].section?.id)
      const fromSectionId = deliverables[event.currentIndex]?.section?.id

      if (section != null) {
        if (!this.scopeTabService.sectionSelectedStates[section.id]) {
          this.scopeTabService.toggleSection(section.id)
        }
        deliverables[event.currentIndex].section = section.id != null ? { id: section.id } : null
      } else {
        deliverables[event.currentIndex].section = null
      }

      deliverables.forEach((d, i) => (d.order = i))

      this.onMoveDeliverable.emit({
        fromSectionId,
        deliverable: mapScopeDeliverableToPlainApi(deliverables[event.currentIndex]),
      })

      this.store
        .pipe(
          select(ScopeOverviewSelectors.selectUpdateDeliverableLoaded),
          filter((loaded) => loaded),
          take(1)
        )
        .subscribe(() => {
          this.store.dispatch(
            ScopeOverviewActions.updateDeliverablesOrder({
              scopeId: this.scopeVersion.identity.id,
              sectionId: section?.id,
              deliverables,
            })
          )
        })
    } else if (event.previousIndex != event.currentIndex) {
      moveItemInArray(deliverables, event.previousIndex, event.currentIndex)
      this.updateScopeDeliverableOrder(deliverables, section?.id)
    }
  }

  updateScopeDeliverableOrder(data: Deliverable[], sectionId?: number) {
    data.forEach((d, i) => (d.order = i))
    this.store.dispatch(
      ScopeOverviewActions.updateDeliverablesOrder({
        scopeId: this.scopeVersion.identity.id,
        sectionId: sectionId,
        deliverables: data,
      })
    )
  }

  onDragEnter(section: ScopeSection) {
    if (this.scopeElementsService.deliverableDragging) {
      this.scopeElementsService.dragTargetId = section.id
    }
  }

  private isComponentDeliverableEditable(component: ScopeComponent): boolean {
    if (!this.scopeApprovalFlowService.isScopeEditable(this.scopeVersion)) return false
    const deliverable = this.scopeVersion.deliverables.find((d) => d.id === component.deliverable.id)
    return (
      !deliverable?.fixedPricing ||
      (hasFixedCostDeliverableEditPrivileges(this.currentUser, this.scopeVersion) && deliverable.fixedCostEditable)
    )
  }

  setComponentMenuOptions() {
    if (!this.componentMenuOptions)
      this.componentMenuOptions = [
        {
          hasPrivilege: () => Privilege.SCOPE__CREATE_FEES,
          isHidden: () => !this.currentUser.hasPrivilege(Privilege.SCOPE__CREATE_FEES),
          callback: (element) => this.addComponentFee(element),
          icon: () => 'attach_money',
          name: () => `Add fee`,
        },
        {
          isHidden: (component: ScopeComponent) => !this.isComponentDeliverableEditable(component),
          callback: (element) => this.deleteComponent(element),
          name: () => 'Delete',
          icon: () => 'delete',
        },
      ]
  }

  getDeliverablesTableConfig = (sectionId?: number): ScopeElementsConfig => ({
    scopeVersion: this.scopeVersion,
    userColumns: this.isTimeline ? this.scopeElementsService.timelineColumns : this.scopeElementsService.userColumns,
    currency: this.exchangeRateService.selectedCurrency,
    isIndependent: true,
    isDefaultSection: !sectionId,
    showActionsColumn: true,
    isTimeline: this.isTimeline,
    isDeliverablesLoading$: sectionId
      ? this.store.select(ScopeOverviewSelectors.selectIsSectionDeliverablesLoading(sectionId))
      : this.isDeliverablesLoading$,
    isEditable:
      this.scopeApprovalFlowService.isCollaborator(this.scopeVersion) &&
      (!isNil(this.isEditable) ? this.isEditable : this.scopeApprovalFlowService.isScopeEditable(this.scopeVersion)),
    mainColumnClass: this.mainColumnClass,
    componentMainColumnClass: this.componentMainColumnClass,
    roleMainColumnClass: this.roleMainColumnClass,
    deliverableDrops: this.deliverableDrops,
    sectionDrops: this.sectionDrops,
    changeItemsMap$: this.store.pipe(select(ChangeRequestSelectors.selectChangeItemEntities)),
    isChangeRequest: this.isChangeRequest,
    showDiff: this.changeRequestNavigationService.currentTab === ChangeRequestTab.TRACKED,
    showNetDelta: this.changeRequestNavigationService.currentTab === ChangeRequestTab.LIST,
    hasFractionalQuantities: this.authService.loggedInUser.company.hasApplicationSetting(
      ApplicationSetting.COMPONENT__ENABLE_FRACTIONAL_QUANTITIES
    ),
  })

  get deliverableDrops() {
    const str: string[] = []
    this.scopeVersion.deliverables.forEach((s) => {
      str.push('deliverable-' + s.id)
    })
    return str
  }

  protected readonly trackById = trackById
}
